import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Badge, Progress, UncontrolledTooltip } from 'reactstrap';
import { MapPin, ShieldCheck, AlertTriangle } from 'lucide-react';
import { formatCreatedDate } from '../../constants/utils';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';

const EM_DASH = '—';

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** Maps an asset's criticality string to a Bootstrap semantic color name. */
const criticalityColor = (criticality) => {
  switch ((criticality || '').toLowerCase()) {
    case 'critical': case 'high': return 'danger';
    case 'medium': return 'warning';
    case 'low': return 'success';
    default: return 'secondary';
  }
};

/** Uptime threshold buckets — 95% / 85% — match the per-card text-color rule. */
const uptimeColor = (pct) => {
  if (pct == null) return 'secondary';
  if (pct >= 95) return 'success';
  if (pct >= 85) return 'warning';
  return 'danger';
};

/** RUL bucketing: ≤6 months critical, ≤18 months caution, otherwise healthy. */
const rulColor = (months) => {
  if (months == null) return 'secondary';
  if (months <= 6) return 'danger';
  if (months <= 18) return 'warning';
  return 'success';
};

/**
 * Rich card for one asset's maintenance spend over a period. Matches the locked Phase 2
 * mockup design: left criticality stripe, badge cluster (criticality / contract / % of asset
 * value), uptime + RUL + TCO grid, maintenance-spend progress bar (relative to the top spender
 * in {@code maxSpend}), corrective/preventive split bar, and a "Consider replacing" footer
 * band tied to {@code replacementSuggested}.
 *
 * @param {object}  asset      Per-asset spend payload from /reporting/assets/maintenance-spend
 * @param {string}  currency   ISO currency code for money formatting
 * @param {number}  maxSpend   Largest {@code totalSpend} in the visible set; drives the
 *                             relative-spend progress-bar denominator
 */
const AssetSpendCard = ({ asset, currency, maxSpend }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const critColor = criticalityColor(asset.criticality);
  const critKey = asset.criticality ? asset.criticality.toLowerCase() : null;

  const uptimePct = asset.uptimePercent != null ? Number(asset.uptimePercent) : null;
  const upClr = uptimeColor(uptimePct);

  const rulMonths = asset.remainingUsefulLifeMonths;
  const rlClr = rulColor(rulMonths);

  const correctivePct = asset.correctivePct != null
    ? Math.round(asset.correctivePct * 100) : null;
  const preventivePct = correctivePct != null ? 100 - correctivePct : null;

  const pctOfAssetValue = (asset.lifetimeMaintenanceCost != null
    && asset.assetCost != null && Number(asset.assetCost) > 0)
    ? Math.round((Number(asset.lifetimeMaintenanceCost) / Number(asset.assetCost)) * 100)
    : null;

  // Relative spend bar — denominator is the top spender in the visible set (so the highest
  // bar always fills, lower spenders scale proportionally). Defensive against maxSpend=0.
  const spendBarPct = (maxSpend && maxSpend > 0 && asset.totalSpend != null)
    ? Math.min(100, (Number(asset.totalSpend) / maxSpend) * 100)
    : 0;

  // Cumulative spend vs replacement value — caps at 100% visually even when the asset is
  // past replacement threshold.
  const cumulativeVsValuePct = pctOfAssetValue != null
    ? Math.min(100, pctOfAssetValue) : null;

  const handleClick = () => {
    if (!asset.assetUuid) return;
    navigate(`/asset-detail/${asset.assetUuid}?tab=cost`);
  };

  const missingPriceId = `missing-price-${asset.assetId}`;

  return (
    <Card
      className={`mb-3 border-start border-4 border-${critColor} shadow-sm`}
      style={{ cursor: asset.assetUuid ? 'pointer' : 'default' }}
      onClick={handleClick}
    >
      <CardBody>
        {/* Header: asset name + badge cluster + spend value */}
        <div className="d-flex justify-content-between align-items-start mb-2 gap-2 flex-wrap">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
              <h6 className="fw-bold mb-0">{asset.assetName || EM_DASH}</h6>
              {asset.criticality && (
                <Badge color={critColor} pill className="text-uppercase">
                  {t(`shared.${critKey}`, asset.criticality)}
                </Badge>
              )}
              {asset.contractActive && (
                <Badge color="primary" pill className="d-inline-flex align-items-center gap-1">
                  <ShieldCheck size={11} />
                  {t('reporting.maintenance_spend_by_asset.contract_active', 'Contract active')}
                </Badge>
              )}
              {pctOfAssetValue != null && (
                <Badge color="warning" pill>
                  {t('reporting.maintenance_spend_by_asset.pct_of_asset_value', '{{pct}}% of asset value', { pct: pctOfAssetValue })}
                </Badge>
              )}
              {asset.hasMissingPriceData && (
                <>
                  <AlertTriangle
                    size={14}
                    className="text-warning"
                    id={missingPriceId}
                    style={{ cursor: 'help' }}
                  />
                  <UncontrolledTooltip placement="top" target={missingPriceId}>
                    {t('reporting.maintenance_spend_by_asset.missing_price_hint', 'Some parts have no unit price set — spend may be understated.')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {asset.locationName && (
              <div className="text-muted small d-flex align-items-center gap-1">
                <MapPin size={12} />
                <span>{asset.locationName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Uptime / RUL / TCO grid */}
        <div className="row g-3 small mb-3">
          <div className="col-md-4">
            <div className="text-muted">{t('reporting.maintenance_spend_by_asset.uptime', 'Uptime')}</div>
            <div className={`fw-semibold text-${upClr}`}>
              {uptimePct != null ? `${uptimePct.toFixed(1)}%` : EM_DASH}
            </div>
            {uptimePct != null && (
              <Progress value={uptimePct} color={upClr} style={{ height: 4 }} className="mt-1" />
            )}
          </div>
          <div className="col-md-4">
            <div className="text-muted">{t('reporting.maintenance_spend_by_asset.rul', 'Remaining useful life')}</div>
            <div className={`fw-semibold text-${rlClr}`}>
              {rulMonths == null
                ? EM_DASH
                : rulMonths < 0
                  ? t('reporting.maintenance_spend_by_asset.rul_past_eol', 'past end-of-life')
                  : t('reporting.maintenance_spend_by_asset.rul_months', '{{count}} months', { count: rulMonths })}
            </div>
          </div>
          <div className="col-md-4">
            <div className="text-muted">{t('reporting.maintenance_spend_by_asset.lifetime_tco', 'Total cost of ownership')}</div>
            <div className="fw-bold">
              {asset.lifetimeTCO != null ? formatMoney(asset.lifetimeTCO, currency) : EM_DASH}
            </div>
          </div>
        </div>

        {/* Maintenance spend (period) bar */}
        <div className="mb-3">
          <div className="d-flex justify-content-between small mb-1">
            <span className="text-muted">{t('reporting.maintenance_spend_by_asset.card_period_total', 'Maintenance spend')}</span>
            <span className="fw-semibold">{formatMoney(asset.totalSpend, currency)}</span>
          </div>
          <Progress value={spendBarPct} color="danger" style={{ height: 6 }} />
          <div className="d-flex flex-wrap gap-3 mt-2 small">
            <div>
              <span className="text-muted">{t('reporting.cost.summary.labor', 'Labor')}</span>{' '}
              <span className="fw-semibold">{formatMoney(asset.laborCost, currency)}</span>
            </div>
            <div>
              <span className="text-muted">{t('reporting.cost.summary.parts', 'Parts')}</span>{' '}
              <span className="fw-semibold">{formatMoney(asset.partsCost, currency)}</span>
            </div>
            <div>
              <span className="text-muted">{t('shared.wos', 'WOs')}</span>{' '}
              <span className="fw-semibold">{asset.workOrderCount ?? 0}</span>
            </div>
            {asset.lastWorkOrderAt && (
              <div>
                <span className="text-muted">{t('reporting.maintenance_spend_by_asset.last_wo', 'Last WO')}</span>{' '}
                <span className="fw-semibold">{formatCreatedDate(asset.lastWorkOrderAt)}</span>
              </div>
            )}
          </div>
          {correctivePct != null && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <Progress multi style={{ height: 6, flex: 1 }}>
                <Progress bar value={correctivePct} color="danger" />
                <Progress bar value={preventivePct} color="success" />
              </Progress>
              <small className="text-danger fw-semibold">{correctivePct}%</small>
              <small className="text-muted">{t('shared.global_org.work_type_corrective', 'Corrective').toLowerCase()}</small>
              <small className="text-success fw-semibold">{preventivePct}%</small>
              <small className="text-muted">{t('shared.preventive', 'Preventive').toLowerCase()}</small>
            </div>
          )}
        </div>

        {/* Cumulative spend vs replacement value — gated on having an assetCost. */}
        {cumulativeVsValuePct != null && (
          <div className="border-top pt-2">
            <div className="d-flex justify-content-between align-items-center small mb-1">
              <span className="text-muted">
                {t('reporting.maintenance_spend_by_asset.cumulative_vs_replacement', 'Cumulative spend vs replacement value')}
              </span>
              {asset.replacementSuggested && (
                <span className="text-danger fw-semibold d-inline-flex align-items-center gap-1">
                  <AlertTriangle size={12} />
                  {t('reporting.maintenance_spend_by_asset.consider_replacing', 'Consider replacing — cost threshold exceeded')}
                </span>
              )}
            </div>
            <Progress
              value={cumulativeVsValuePct}
              color={asset.replacementSuggested ? 'danger' : 'success'}
              style={{ height: 4 }}
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default AssetSpendCard;
